// Browser side of the local Storage backup: talks to the storageBackup function and writes
// files into a folder the admin picked (File System Access API, Chrome/Edge only).
import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';
import { LOCAL_MANIFEST_FILE, toLocalPath } from './backupPlan';

const STORAGE_BACKUP_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/storageBackup';
const SIGN_BATCH_SIZE = 100;
const DOWNLOAD_CONCURRENCY = 12;
const MANIFEST_SAVE_INTERVAL_MS = 15000;

const DB_NAME = 'guidesofia-backup';
const STORE_NAME = 'handles';
const HANDLE_KEY = 'backupFolder';
const SUMMARY_KEY = 'guidesofia-backup-summary';

export const isLocalBackupSupported = () => typeof window !== 'undefined' && 'showDirectoryPicker' in window;

const callStorageBackup = async (body) => {
  const token = await getFirebaseAuthToken();
  const response = await fetch(STORAGE_BACKUP_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body)
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || `Backup request failed (${response.status})`);
  return payload;
};

export const fetchRemoteManifest = () => callStorageBackup({ action: 'manifest' });

// --- Remembering the chosen folder between visits (IndexedDB can store directory handles) ---

const openDb = () => new Promise((resolve, reject) => {
  const request = indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (mode, action) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = action(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const loadSavedFolder = () => withStore('readonly', (store) => store.get(HANDLE_KEY)).catch(() => null);
export const saveFolder = (handle) => withStore('readwrite', (store) => store.put(handle, HANDLE_KEY));

export const hasFolderPermission = async (handle, { request = false } = {}) => {
  const options = { mode: 'readwrite' };
  if ((await handle.queryPermission(options)) === 'granted') return true;
  return request && (await handle.requestPermission(options)) === 'granted';
};

// --- Local manifest: what we downloaded, so later runs only fetch what changed ---

export const readLocalManifest = async (folder) => {
  try {
    const file = await (await folder.getFileHandle(LOCAL_MANIFEST_FILE)).getFile();
    const manifest = JSON.parse(await file.text());
    return { lastBackupAt: null, lastRunAt: null, files: {}, ...manifest };
  } catch {
    return { lastBackupAt: null, lastRunAt: null, files: {} };
  }
};

const writeLocalManifest = async (folder, manifest) => {
  const writable = await (await folder.getFileHandle(LOCAL_MANIFEST_FILE, { create: true })).createWritable();
  await writable.write(JSON.stringify(manifest));
  await writable.close();
};

// Small summary for the header tab, so it can show the backup age without folder access
export const readBackupSummary = () => {
  try {
    return JSON.parse(localStorage.getItem(SUMMARY_KEY)) || null;
  } catch {
    return null;
  }
};

export const saveBackupSummary = (summary) => {
  try {
    localStorage.setItem(SUMMARY_KEY, JSON.stringify(summary));
    window.dispatchEvent(new Event('guidesofia-backup-summary'));
  } catch {
    // Summary is a convenience only
  }
};

// Files already in the folder but missing from the manifest (manifest lost, reset, or the folder
// was copied in by hand) are adopted instead of downloaded again. A file counts as a good copy when
// its size matches and it was written after the Storage object last changed.
export const adoptExistingFiles = async ({ folder, manifest, objects, onProgress = () => {} }) => {
  const candidates = objects.filter((object) => !manifest.files[object.n]);
  const directories = new Map([['', Promise.resolve(folder)]]);
  const getDirectory = (path) => {
    if (!directories.has(path)) {
      const slash = path.lastIndexOf('/');
      const parentPath = slash === -1 ? '' : path.slice(0, slash);
      const lookup = getDirectory(parentPath).then((parent) => parent.getDirectoryHandle(path.slice(slash + 1)));
      lookup.catch(() => {});
      directories.set(path, lookup);
    }
    return directories.get(path);
  };

  let adopted = 0;
  let checked = 0;
  let next = 0;
  const worker = async () => {
    while (next < candidates.length) {
      const object = candidates[next++];
      const localPath = toLocalPath(object.n);
      const slash = localPath.lastIndexOf('/');
      try {
        const directory = await getDirectory(slash === -1 ? '' : localPath.slice(0, slash));
        const file = await (await directory.getFileHandle(localPath.slice(slash + 1))).getFile();
        const updatedAt = Date.parse(object.u) || 0;
        if (file.size === object.s && file.lastModified >= updatedAt) {
          manifest.files[object.n] = { m: object.m, s: object.s, u: object.u, p: localPath, at: new Date(file.lastModified).toISOString() };
          adopted += 1;
        }
      } catch {
        // Not on disk yet - it stays in the download list
      }
      checked += 1;
      if (checked % 200 === 0 || checked === candidates.length) onProgress({ checked, total: candidates.length, adopted });
    }
  };
  await Promise.all(Array.from({ length: DOWNLOAD_CONCURRENCY }, worker));

  if (adopted) await writeLocalManifest(folder, manifest);
  return { checked, adopted };
};

const getFileHandleForPath = async (folder, localPath) => {
  const parts = localPath.split('/');
  let directory = folder;
  for (const part of parts.slice(0, -1)) {
    directory = await directory.getDirectoryHandle(part, { create: true });
  }
  return directory.getFileHandle(parts[parts.length - 1], { create: true });
};

// Downloads the given objects into the folder, recording each one in the local manifest as it lands.
// Safe to stop and resume: finished files are saved to the manifest every few seconds.
export const downloadObjects = async ({ folder, manifest, objects, onProgress, shouldStop }) => {
  const failures = [];
  const counts = { doneFiles: 0, doneBytes: 0 };
  // Saves run in the background so downloads never wait on the (multi-MB) manifest,
  // but they are chained so two saves never write the file at the same time
  let manifestSave = Promise.resolve();
  let lastSaveAt = Date.now();
  const queueManifestSave = () => {
    lastSaveAt = Date.now();
    manifestSave = manifestSave.then(() => writeLocalManifest(folder, manifest)).catch(() => {});
  };

  // URLs are signed 100 at a time and one batch ahead, so workers never sit idle waiting for the function
  const signedBatches = new Map();
  const signBatch = (index) => {
    if (index * SIGN_BATCH_SIZE >= objects.length) return null;
    if (!signedBatches.has(index)) {
      const names = objects.slice(index * SIGN_BATCH_SIZE, (index + 1) * SIGN_BATCH_SIZE).map((object) => object.n);
      const signing = callStorageBackup({ action: 'sign', names })
        .then((signed) => new Map(signed.urls.map((entry) => [entry.n, entry.url])));
      signing.catch(() => {}); // a prefetched batch may fail before anyone awaits it
      signedBatches.set(index, signing);
    }
    return signedBatches.get(index);
  };
  const getSignedUrl = async (position) => {
    const batchIndex = Math.floor(position / SIGN_BATCH_SIZE);
    signBatch(batchIndex + 1);
    signedBatches.delete(batchIndex - 2);
    return (await signBatch(batchIndex)).get(objects[position].n);
  };

  let next = 0;
  const worker = async () => {
    while (next < objects.length && !shouldStop()) {
      const position = next++;
      const object = objects[position];
      const localPath = toLocalPath(object.n);
      try {
        const response = await fetch(await getSignedUrl(position));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const writable = await (await getFileHandleForPath(folder, localPath)).createWritable();
        await response.body.pipeTo(writable);

        manifest.files[object.n] = { m: object.m, s: object.s, u: object.u, p: localPath, at: new Date().toISOString() };
        counts.doneFiles += 1;
        counts.doneBytes += object.s;
        if (Date.now() - lastSaveAt >= MANIFEST_SAVE_INTERVAL_MS) queueManifestSave();
      } catch (error) {
        failures.push({ name: object.n, error: error.message });
      }
      onProgress({ doneFiles: counts.doneFiles, doneBytes: counts.doneBytes, failed: failures.length });
    }
  };
  await Promise.all(Array.from({ length: DOWNLOAD_CONCURRENCY }, worker));

  manifest.lastRunAt = new Date().toISOString();
  const finished = !shouldStop() && failures.length === 0 && counts.doneFiles === objects.length;
  if (finished) manifest.lastBackupAt = manifest.lastRunAt;
  await manifestSave;
  await writeLocalManifest(folder, manifest);

  return { doneFiles: counts.doneFiles, doneBytes: counts.doneBytes, failures, finished };
};
